'use client';

import { Children, useId, useState } from 'react';
import { Dropdown, Form } from 'react-bootstrap';
import { Eye, EyeSlash } from 'react-bootstrap-icons';
import { useController } from 'react-hook-form';

function joinClasses(...parts) {
  return parts.filter(Boolean).join(' ');
}

function renderSelectOptions(options = []) {
  return options.map((option) => (
    <option
      key={`${option.value}`}
      value={option.value}
      disabled={Boolean(option.disabled)}
    >
      {option.label}
    </option>
  ));
}

export function FormField({
  label,
  as = 'input',
  type = 'text',
  id,
  name,
  description = '',
  children = null,
  options = [],
  placeholder = '',
  rows = 3,
  hideLabel = false,
  centerLabel = false,
  helperText = '',
  error = '',
  fieldClassName = 'form-field',
  labelClassName = '',
  controlClassName = 'ps-4',
  radioControlClassName = '',
  inputClassName = 'ps-4',
  radioInputClassName = '',
  helperClassName = 'form-text form-field__helper ms-3',
  errorClassName = 'invalid-feedback d-block',
  controlWrapClassName = '',
  optionClassName = '',
  optionInputClassName = '',
  optionBodyClassName = '',
  renderOption = null,
  showRequiredAsterisk = false,
  asteriskPosition = 'left',
  passwordToggle = false,
  showPasswordLabel = 'Show password',
  hidePasswordLabel = 'Hide password',
  selectPlaceholder = '',
  spellCheck = false,
  inputRef = null,
  ...controlProps
}) {
  const generatedId = useId();
  const [showPassword, setShowPassword] = useState(false);
  const controlId = id || name || `field-${generatedId}`;
  const helperId = helperText ? `${controlId}-helper` : '';
  const errorId = error ? `${controlId}-error` : '';
  const describedBy = [controlProps['aria-describedby'], helperId, errorId]
    .filter(Boolean)
    .join(' ');
  const hasError = Boolean(error);
  const controlLabel = label || controlProps['aria-label'] || '';
  const isChoiceField =
    as === 'checkbox' || as === 'switch' || as === 'radio-group';
  const wrapperClassName = joinClasses(
    passwordToggle && type === 'password'
      ? 'input-with-toggle'
      : 'field-with-asterisk',
    as === 'radio-group' ? 'radio-group' : '',
    hasError ? 'invalid' : '',
    controlWrapClassName,
  );
  const labelClasses = joinClasses(
    'form-group-label form-label',
    labelClassName,
    centerLabel ? 'text-center d-block mx-0' : '',
  );

  const defaultControlClassName =
    as === 'select' ? 'form-select' : isChoiceField ? '' : 'form-control';
  const labelContent = label ? (
    <>
      <span className="form-field__label-text">{label}</span>
      {showRequiredAsterisk ? (
        <span
          className="required-asterisk form-field__label-asterisk"
          aria-hidden="true"
        >
          *
        </span>
      ) : null}
    </>
  ) : null;
  const commonControlProps = {
    id: controlId,
    name,
    placeholder,
    ref: inputRef,
    spellCheck,
    'aria-describedby': describedBy || undefined,
    'aria-invalid': hasError || controlProps['aria-invalid'] ? true : undefined,
    ...controlProps,
  };

  if (hideLabel && controlLabel && !commonControlProps['aria-label']) {
    commonControlProps['aria-label'] = controlLabel;
  }

  let control = as === 'select' ? null : children;

  if (!control) {
    if (as === 'textarea') {
      control = (
        <textarea
          rows={rows}
          {...commonControlProps}
          className={joinClasses(
            defaultControlClassName,
            inputClassName,
            hasError ? 'is-invalid' : '',
            'rounded-3',
          )}
        />
      );
    } else if (as === 'select') {
      // Build option list from either the options prop or <option> children
      const parsedFromChildren = Children.toArray(children)
        .filter(
          (child) =>
            child && typeof child === 'object' && child.type === 'option',
        )
        .map((child) => ({
          value: child.props.value,
          label:
            typeof child.props.children === 'string'
              ? child.props.children
              : String(child.props.value ?? ''),
          disabled: Boolean(child.props.disabled),
        }));

      const optionsList =
        parsedFromChildren.length > 0
          ? parsedFromChildren
          : options.map((o) => ({
              value: o.value,
              label: o.label,
              disabled: Boolean(o.disabled),
            }));

      const currentValue = String(controlProps.value ?? '');
      const selectedOption = optionsList.find(
        (o) => String(o.value) === currentValue,
      );
      const toggleText =
        selectedOption?.label || selectPlaceholder || placeholder || '\u00a0'; // non-breaking space keeps height when nothing selected

      control = (
        <Dropdown
          onSelect={(eventKey) => {
            if (commonControlProps.onChange) {
              commonControlProps.onChange({
                target: { value: eventKey ?? '', name },
              });
            }
          }}
          className="form-select-dropdown w-100"
        >
          <Dropdown.Toggle
            id={controlId}
            variant="outline-info"
            disabled={Boolean(commonControlProps.disabled)}
            aria-describedby={commonControlProps['aria-describedby']}
            aria-invalid={commonControlProps['aria-invalid']}
            className={joinClasses(
              'form-select-dropdown__toggle w-100 ps-4',
              hasError ? 'is-invalid' : '',
            )}
          >
            <span className="form-select-dropdown__value">{toggleText}</span>
          </Dropdown.Toggle>
          <Dropdown.Menu className="form-select-dropdown__menu w-100">
            {optionsList.map((option) => (
              <Dropdown.Item
                key={String(option.value)}
                eventKey={String(option.value)}
                active={String(option.value) === currentValue}
                disabled={option.disabled}
              >
                {option.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      );
    } else if (as === 'checkbox' || as === 'switch') {
      control = (
        <label
          className={joinClasses(
            'form-check form-choice',
            as === 'switch' ? 'form-switch form-choice--switch' : '',
            radioControlClassName,
            hasError ? 'is-invalid' : '',
          )}
          htmlFor={controlId}
        >
          <input
            {...commonControlProps}
            type="checkbox"
            role={as === 'switch' ? 'switch' : undefined}
            checked={Boolean(controlProps.checked)}
            className={joinClasses(
              'form-check-input form-choice__input',
              radioInputClassName,
            )}
          />
          <span className="form-choice__body">
            {label ? <span className="form-choice__label">{label}</span> : null}
            {description ? (
              <small
                className={joinClasses(
                  'form-choice__description',
                  helperClassName,
                )}
              >
                {description}
              </small>
            ) : helperText ? (
              <small
                id={helperId}
                className={joinClasses(
                  'form-choice__description',
                  helperClassName,
                )}
              >
                {helperText}
              </small>
            ) : null}
          </span>
        </label>
      );
    } else if (as === 'radio-group') {
      const selectedValue = controlProps.value ?? '';

      control = (
        <div
          className={joinClasses(
            'form-radio-group',
            controlClassName,
            hasError ? 'is-invalid' : '',
          )}
          role="radiogroup"
          aria-label={hideLabel ? controlLabel : undefined}
          aria-labelledby={
            !hideLabel && label ? `${controlId}-label` : undefined
          }
          aria-describedby={describedBy || undefined}
        >
          {options.map((option, index) => {
            const checked = String(selectedValue) === String(option.value);
            const optionId = `${controlId}-${index}`;

            return (
              <label
                key={`${option.value}`}
                className={joinClasses(
                  'form-radio-option',
                  optionClassName,
                  checked ? 'is-selected' : '',
                  option.disabled ? 'is-disabled' : '',
                  option.className,
                )}
                htmlFor={optionId}
              >
                <input
                  type="radio"
                  id={optionId}
                  name={name || controlId}
                  value={option.value}
                  checked={checked}
                  disabled={Boolean(option.disabled)}
                  onChange={controlProps.onChange}
                  onBlur={controlProps.onBlur}
                  ref={index === 0 ? inputRef : undefined}
                  className={joinClasses(
                    'form-check-input form-radio-option__input',
                    optionInputClassName,
                  )}
                />
                {renderOption ? (
                  renderOption(option, { checked, id: optionId })
                ) : (
                  <span
                    className={joinClasses(
                      'form-radio-option__body',
                      optionBodyClassName,
                    )}
                  >
                    <span className="form-radio-option__topline">
                      <span className="form-radio-option__label">
                        {option.label}
                      </span>
                      {option.badge ? (
                        <span className="form-radio-option__badge">
                          {option.badge}
                        </span>
                      ) : null}
                    </span>
                    {option.description ? (
                      <span className="form-radio-option__description">
                        {option.description}
                      </span>
                    ) : null}
                    {option.helper ? (
                      <span className="form-radio-option__helper">
                        {option.helper}
                      </span>
                    ) : null}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      );
    } else {
      control = (
        <input
          type={
            passwordToggle && type === 'password' && showPassword
              ? 'text'
              : type
          }
          {...commonControlProps}
          className={joinClasses(
            defaultControlClassName,
            controlClassName,
            inputClassName,
            hasError ? 'is-invalid' : '',
          )}
        />
      );
    }
  }

  if (isChoiceField) {
    return (
      <Form.Group className={fieldClassName}>
        {as === 'radio-group' && label && !hideLabel ? (
          <Form.Label id={`${controlId}-label`} className={labelClasses}>
            {labelContent}
          </Form.Label>
        ) : null}

        {control}

        {as === 'radio-group' && helperText ? (
          <Form.Text id={helperId} className={helperClassName}>
            {helperText}
          </Form.Text>
        ) : null}

        {hasError ? (
          <div id={errorId} className={errorClassName}>
            {error}
          </div>
        ) : null}
      </Form.Group>
    );
  }

  return (
    <Form.Group className={fieldClassName} controlId={controlId}>
      {label && !hideLabel ? (
        <Form.Label className={labelClasses}>{labelContent}</Form.Label>
      ) : null}

      <div className={wrapperClassName} data-asterisk-pos={asteriskPosition}>
        {control}

        {showRequiredAsterisk && hideLabel ? (
          <span
            className="required-asterisk form-field__overlay-asterisk"
            aria-hidden="true"
          >
            *
          </span>
        ) : null}

        {passwordToggle && type === 'password' ? (
          <button
            type="button"
            className="password-toggle-btn"
            onClick={() => setShowPassword((current) => !current)}
            aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
          >
            {showPassword ? <EyeSlash /> : <Eye />}
          </button>
        ) : null}
      </div>

      {helperText ? (
        <Form.Text id={helperId} className={helperClassName}>
          {helperText}
        </Form.Text>
      ) : null}

      {hasError ? (
        <div id={errorId} className={errorClassName}>
          {error}
        </div>
      ) : null}
    </Form.Group>
  );
}

export function RHFFormField({
  control,
  name,
  rules,
  defaultValue,
  shouldUnregister,
  onChange,
  ...fieldProps
}) {
  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const sharedProps = {
    name,
    onBlur: field.onBlur,
    inputRef: field.ref,
    error: fieldState.error?.message || fieldProps.error || '',
  };

  if (fieldProps.as === 'checkbox' || fieldProps.as === 'switch') {
    return (
      <FormField
        {...fieldProps}
        {...sharedProps}
        checked={Boolean(field.value)}
        onChange={(event) => {
          field.onChange(event.target.checked);
          onChange?.(event);
        }}
      />
    );
  }

  if (fieldProps.as === 'radio-group') {
    return (
      <FormField
        {...fieldProps}
        {...sharedProps}
        value={field.value ?? ''}
        onChange={(event) => {
          field.onChange(event.target.value);
          onChange?.(event);
        }}
      />
    );
  }

  return (
    <FormField
      {...fieldProps}
      {...sharedProps}
      value={field.value ?? ''}
      onChange={(event) => {
        field.onChange(event);
        onChange?.(event);
      }}
    />
  );
}

export function FormGrid({ children, className = '' }) {
  return <div className={`form-grid ${className}`.trim()}>{children}</div>;
}

export function FormActions({ children, className = '' }) {
  return <div className={`form-actions ${className}`.trim()}>{children}</div>;
}
